import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {withState, t} from 'shared/core';
import {auth} from 'main/tools';
import {HRDashboardPageState} from '../state';
import {Layout, Display, Button, QuickTip} from 'shared/components';
import {FormattedMessage} from 'react-intl';
import DragAndDropContext from 'shared/components/dnd/DragAndDropContext';
import {renderWidget, isWidgetVisible} from './widgetRegistry';
import {DraggableTile, Column} from './DraggableTile';
import {Feed} from '../feed';

const EditModeToolbar = observer(({editMode, onEdit, onSave, onCancel, saving}) => {
  if (!auth.user.admin) return null;

  if (!editMode) {
    return (
      <Button trait='default' size='sm' onClick={onEdit}>
        <i className='material-symbols-solid mr1 h3 align-middle'>{'edit'}</i>
        {t('hr_dashboard.Edit layout')}
      </Button>
    );
  }

  return (
    <React.Fragment>
      <Button trait='default' size='sm' className='mr1' onClick={onCancel} disabled={saving}>
        {t('hr_dashboard.Cancel')}
      </Button>
      <Button trait='primary' size='sm' onClick={onSave} disabled={saving}>
        {t('hr_dashboard.save_layout')}
      </Button>
    </React.Fragment>
  );
});

const WidgetSlot = observer(({widgetKey, column, index, editMode, moveTile, uiState}) => {
  if (!isWidgetVisible(widgetKey, uiState)) return null;

  const content = renderWidget(widgetKey, uiState);

  if (!editMode) return content;

  return (
    <DraggableTile widgetKey={widgetKey} column={column} index={index} moveTile={moveTile}>
      {content}
    </DraggableTile>
  );
});

const HRDashboardPage = observer(({uiState}) => {
  const [editMode, setEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState(null);
  const [saving, setSaving] = useState(false);

  const persistedLayout = uiState.widgetLayout.layout;
  const activeLayout = editMode ? draftLayout : persistedLayout;

  const enterEditMode = () => {
    setDraftLayout({left: [...persistedLayout.left], right: [...persistedLayout.right]});
    setEditMode(true);
  };

  const cancelEdit = () => {
    setDraftLayout(null);
    setEditMode(false);
  };

  const saveLayout = async () => {
    setSaving(true);
    await uiState.saveWidgetLayout(draftLayout);
    setDraftLayout(null);
    setEditMode(false);
    setSaving(false);
  };

  const moveTile = ({fromColumn, fromIndex, toColumn, toIndex}) => {
    setDraftLayout(prev => {
      const next = {left: [...prev.left], right: [...prev.right]};
      const [moved] = next[fromColumn].splice(fromIndex, 1);
      next[toColumn].splice(toIndex, 0, moved);
      return next;
    });
  };

  return (
    <React.Fragment>
      <Display
        title={uiState.greeting}
        documentTitle={t('hr_dashboard.Home')}
        toolbar={
          <EditModeToolbar
            editMode={editMode}
            onEdit={enterEditMode}
            onSave={saveLayout}
            onCancel={cancelEdit}
            saving={saving}
          />
        }
      />
      <Layout>
        {editMode && (
          <div className='mb2'>
            <QuickTip trait='info' header={<FormattedMessage id='hr_dashboard.Editing company home page layout'/>}>
              <FormattedMessage id='hr_dashboard.Drag widgets to rearrange them or move them between columns. Changes apply to everyone in the company.'/>
            </QuickTip>
          </div>
        )}
        <div className={`HRDashboardWidgets nm ${editMode ? 'HRDashboardWidgets--editing' : ''}`}>
          <Column
            column='left'
            moveTile={moveTile}
            tileCount={activeLayout.left.length}
            className='HRDashboardWidgets-mainColumn'
          >
            {activeLayout.left.map((widgetKey, index) => (
              <WidgetSlot
                key={widgetKey}
                widgetKey={widgetKey}
                column='left'
                index={index}
                editMode={editMode}
                moveTile={moveTile}
                uiState={uiState}
              />
            ))}
          </Column>
          <div className='HRDashboardWidgets-feed'>
            <Feed emojiReady={uiState.emojiReady}/>
          </div>
          <Column
            column='right'
            moveTile={moveTile}
            tileCount={activeLayout.right.length}
            className='LoosePanelStack sidebar'
          >
            {activeLayout.right.map((widgetKey, index) => (
              <WidgetSlot
                key={widgetKey}
                widgetKey={widgetKey}
                column='right'
                index={index}
                editMode={editMode}
                moveTile={moveTile}
                uiState={uiState}
              />
            ))}
          </Column>
        </div>
      </Layout>
    </React.Fragment>
  );
});

export default withState(DragAndDropContext(HRDashboardPage), HRDashboardPageState);
